import React from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { Star, Clock, Heart, MessageSquare, Briefcase, GraduationCap } from "lucide-react";
import { useFavorites } from "../../context/FavoritesContext";
import ExpertTierBadge from "./ExpertTierBadge";

export default function ExpertCard({ expert }) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorite = isFavorite(expert.id);

  const isPractitioner = expert.type === "practitioner";

  const handleFavoriteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(expert.id);
  };

  return (
    <Link to={`/experts/${expert.id}`} className="block h-full">
      <motion.div
        id={`expert-card_${expert.id}`}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        whileHover={{ y: -2 }}
        transition={{ duration: 0.3 }}
        className={`h-full flex flex-col bg-neutral-900/50 rounded-md overflow-hidden group cursor-pointer ${
          expert.expertTier && expert.expertTier !== "regular"
            ? "border-2 border-amber-500/30"
            : "border border-neutral-800/40"
        }`}
      >
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={expert.avatar}
            alt={expert.name}
            className="absolute inset-0 w-full h-full object-cover"
          />

          <div className="absolute top-3 left-3 z-[5] flex flex-col gap-1.5">
            {expert.expertTier && expert.expertTier !== "regular" ? (
              <ExpertTierBadge tier={expert.expertTier} size="md" />
            ) : (
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md backdrop-blur-sm text-lg font-extralight ${
                  isPractitioner
                    ? "bg-accent-700/70 text-accent-100"
                    : "bg-primary-700/70 text-primary-100"
                }`}
              >
                {isPractitioner ? (
                  <Briefcase className="w-3.5 h-3.5" />
                ) : (
                  <GraduationCap className="w-3.5 h-3.5" />
                )}
                {isPractitioner ? "Бизнес" : "Эксперт"}
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={handleFavoriteClick}
            className={`absolute top-3 right-3 z-[5] p-2 rounded-md transition-all ${
              favorite
                ? "bg-warm-500 text-white shadow-lg"
                : "bg-neutral-900/60 backdrop-blur-sm text-neutral-300 hover:text-white hover:bg-neutral-900/80"
            }`}
            style={{ position: "absolute", top: "12px", right: "12px" }}
          >
            <Heart className={`w-5 h-5 ${favorite ? "fill-current" : ""}`} />
          </button>

          {!expert.available && (
            <div className="absolute bottom-3 left-3 z-[5] px-3 py-1 rounded-md bg-neutral-900/70 backdrop-blur-sm text-lg font-extralight text-neutral-300">
              Недоступен
            </div>
          )}
        </div>

        <div className="p-5 flex flex-col flex-1">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="text-xl font-medium text-neutral-100 mb-1">
                {expert.name}
              </h3>
              <p className="text-lg font-extralight text-neutral-500">
                {expert.title}
              </p>
            </div>
            <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-neutral-800/50">
              <Star className="w-4 h-4 text-warm-400 fill-warm-400" />
              <span className="text-lg font-medium text-neutral-200">
                {expert.rating}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {expert.specializations.slice(0, 3).map((spec) => (
              <span
                key={spec}
                className="px-2.5 py-0.5 rounded-md bg-neutral-800/40 text-neutral-400 text-lg font-extralight"
              >
                {spec}
              </span>
            ))}
          </div>

          <div className="flex items-center gap-4 mb-4 text-lg font-extralight text-neutral-500">
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{expert.experience} лет</span>
            </div>
            <div className="flex items-center gap-1">
              <MessageSquare className="w-4 h-4" />
              <span>
                {expert.reviewsCount !== undefined
                  ? expert.reviewsCount
                  : expert.reviews?.length || 0}{" "}
                отзывов
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-neutral-800/30 mt-auto">
            <div>
              <span className="text-2xl font-medium text-neutral-100">
                {expert.hourlyRate.toLocaleString("ru-RU")} ₽
              </span>
              <span className="text-lg font-extralight text-neutral-500">
                {" "}
                / час
              </span>
            </div>
            <span className="px-4 py-2 rounded-md bg-primary-700 group-hover:bg-primary-600 text-white text-lg font-extralight transition-colors">
              Подробнее
            </span>
          </div>
        </div>
      </motion.div>
    </Link>
  );
}
